import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import RedditPixel from 'react-reddit-pixel';

import { useCurrentTheme } from 'app/shared/theme';
import { useAnalyticsContext } from 'app/shared/utils';
import { Event, EventAttendee } from 'app/shared/typings';
import { AuthContext } from 'app/shared/context/Auth';
import { Box } from 'app/shared/components/atoms';
import { AlternativesDivider } from 'app/shared/components/molecules';
import {
  useTrackProductAdded,
  useTrackVirtualWalletPaymentButtonShown,
  useTrackVirtualWalletPaymentStarted,
} from 'app/fan/utils';
import {
  StripeVirtualWallet,
  VisaPartnershipCheckoutLogic,
} from 'app/fan/utils/checkout';
import {
  PAYMENT_METHOD_APPLE_PAY,
  PAYMENT_METHOD_GOOGLE_PAY,
  shouldDisplayAdditionalPurchaseConfirmationModal,
  ThirdPartyPaymentMethodType,
} from 'app/fan/utils/checkout';
import { ApplePayButton, GooglePayButton } from 'app/fan/components/molecules';
import { AdditionalPurchaseConfirmationModal } from 'app/fan/components/organisms/AdditionalPurchaseConfirmationModal';

interface Props {
  finalPrice: number;
  event: Event | null;
  eventAttendee?: EventAttendee | null;
  stripeVirtualWallet?: StripeVirtualWallet;
  visaPartnershipCheckoutLogic?: VisaPartnershipCheckoutLogic;
  showGooglePay: boolean;
  showApplePay: boolean;
  isExpressCheckout: boolean;
  setOptinGuestMarketing?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const VirtualWalletButtons: React.FC<React.PropsWithChildren<Props>> = ({
  finalPrice,
  event,
  eventAttendee,
  stripeVirtualWallet,
  visaPartnershipCheckoutLogic,
  showApplePay,
  showGooglePay,
  isExpressCheckout,
  setOptinGuestMarketing,
}) => {
  useTrackVirtualWalletPaymentButtonShown({
    isExpressCheckout,
    showApplePay,
    showGooglePay,
    event,
    eventAttendee,
    visaPartnershipCheckoutLogic,
  });
  const [confirmedAdditionalPurchase, setConfirmedAdditionalPurchase] =
    useState(false);
  const { trackAnalyticsEvent } = useAnalyticsContext();

  const trackVirtualWalletPaymentStarted = useTrackVirtualWalletPaymentStarted({
    isExpressCheckout,
    event,
    eventAttendee,
    visaPartnershipCheckoutLogic,
  });
  const paymentRequest = stripeVirtualWallet?.paymentRequest;
  const paymentMethodType: ThirdPartyPaymentMethodType = showGooglePay
    ? PAYMENT_METHOD_GOOGLE_PAY
    : PAYMENT_METHOD_APPLE_PAY;
  const [
    displayAdditionalPurchaseConfirmationModal,
    setDisplayAdditionalPurchaseConfirmationModal,
  ] = useState(false);
  const { user } = useContext(AuthContext);
  const { trackProductAdded } = useTrackProductAdded(event, true);
  const { colors } = useCurrentTheme();
  const { formatMessage } = useIntl();

  if (finalPrice === 0 || (!showApplePay && !showGooglePay) || !event) {
    return null;
  }

  function handleButtonClick() {
    RedditPixel.init('a2_ekhq3a2jqrod');
    RedditPixel.track('AddToCart');
    if (
      shouldDisplayAdditionalPurchaseConfirmationModal(user?.id, event?.id) &&
      !confirmedAdditionalPurchase
    ) {
      trackAnalyticsEvent('Purchase Confirmation Modal Displayed', {});
      setDisplayAdditionalPurchaseConfirmationModal(true);
      setConfirmedAdditionalPurchase(true);
    } else if (!paymentRequest) {
      return;
    } else {
      setOptinGuestMarketing && setOptinGuestMarketing(false);
      trackProductAdded(paymentMethodType);
      trackVirtualWalletPaymentStarted(paymentMethodType);
      paymentRequest.show();
      RedditPixel.track('Purchase');
    }
  }

  return (
    <>
      <Box gap={6} mb={6} flexDirection="column" alignItems="center">
        <ApplePayButton
          onClick={() => handleButtonClick()}
          showButton={showApplePay}
        />
        <GooglePayButton
          onClick={() => handleButtonClick()}
          showButton={showGooglePay}
        />
        <AlternativesDivider
          content={
            isExpressCheckout
              ? undefined
              : formatMessage({ id: 'checkout.payment.orPayWithCard' })
          }
          color={colors.accent.primaryAccent.base}
          textColor={colors.primary.black.base}
          m={3}
          maxWidth={isExpressCheckout ? '260px' : undefined}
          w={isExpressCheckout ? '100%' : undefined}
          backgroundColor={colors.primary.white.base}
        />
      </Box>
      {displayAdditionalPurchaseConfirmationModal && (
        <AdditionalPurchaseConfirmationModal
          isShowing={displayAdditionalPurchaseConfirmationModal}
          setIsShowing={setDisplayAdditionalPurchaseConfirmationModal}
          confirmationAction={handleButtonClick}
        />
      )}
    </>
  );
};
